import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useBudget } from '../../context/BudgetContext';
import { addIncome, removeIncome } from '../../context/budgetActions';
import { FREQUENCIES, FREQUENCY_LABELS } from '../../utils/frequency';
import { formatCAD } from '../../utils/formatCurrency';
import { toMonthly } from '../../utils/frequency';

const INCOME_SUGGESTIONS = {
  es: ['Empleo', 'Trabajo independiente', 'Inversiones', 'Pensión', 'Alquiler', 'Otro'],
  en: ['Employment', 'Self-Employment', 'Investments', 'Pension', 'Rental Income', 'Other'],
  fr: ['Emploi', 'Travail indépendant', 'Investissements', 'Pension', 'Revenus locatifs', 'Autre'],
};

export default function StepIncome() {
  const { t, i18n } = useTranslation();
  const lng = i18n.language || 'es';
  const { state, dispatch } = useBudget();
  const [name, setName] = useState('');
  const [amount, setAmount] = useState('');
  const [frequency, setFrequency] = useState('monthly');
  const [isGross, setIsGross] = useState(false);

  const suggestions = INCOME_SUGGESTIONS[lng] || INCOME_SUGGESTIONS.es;
  const freqLabels = FREQUENCY_LABELS[lng] || FREQUENCY_LABELS.es;

  const handleAdd = () => {
    if (!name.trim() || !amount || Number(amount) <= 0) return;
    dispatch(addIncome({ name: name.trim(), amount: Number(amount), frequency, isGross }));
    setName('');
    setAmount('');
    setFrequency('monthly');
    setIsGross(false);
  };

  const monthlyTotal = state.incomeSources.reduce(
    (sum, s) => sum + toMonthly(s.amount, s.frequency),
    0
  );

  return (
    <Card elevation={2}>
      <CardContent>
        <Typography variant="h5" sx={{ mb: 1, fontWeight: 600, color: '#1E3A5F' }}>
          {t('budgetPlanner.income.title', 'Fuentes de ingreso')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('budgetPlanner.income.subtitle', 'Agrega todas tus fuentes de ingreso mensuales.')}
        </Typography>

        {/* Suggestion chips */}
        <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {suggestions.map((s) => (
            <Chip
              key={s}
              label={s}
              variant={name === s ? 'filled' : 'outlined'}
              color={name === s ? 'primary' : 'default'}
              onClick={() => setName(s)}
              size="small"
            />
          ))}
        </Box>

        {/* Form */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
          <TextField
            label={t('budgetPlanner.income.name', 'Fuente')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            size="small"
            fullWidth
          />
          <TextField
            label={t('budgetPlanner.income.amount', 'Monto ($)')}
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            size="small"
            sx={{ minWidth: 140 }}
            inputProps={{ min: 0, step: 50, inputMode: 'decimal' }}
          />
          <TextField
            label={t('budgetPlanner.income.frequency', 'Frecuencia')}
            select
            value={frequency}
            onChange={(e) => setFrequency(e.target.value)}
            size="small"
            sx={{ minWidth: 150 }}
          >
            {Object.entries(FREQUENCIES).map(([key]) => (
              <MenuItem key={key} value={key}>
                {freqLabels[key]}
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} spacing={2} sx={{ mb: 2 }}>
          <FormControlLabel
            control={<Switch checked={isGross} onChange={(e) => setIsGross(e.target.checked)} size="small" />}
            label={t('budgetPlanner.income.gross', 'Monto bruto (antes de impuestos)')}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAdd}
            disabled={!name.trim() || !amount || Number(amount) <= 0}
            sx={{ bgcolor: '#1E3A5F', '&:hover': { bgcolor: '#1A3152' }, minHeight: 44 }}
          >
            {t('budgetPlanner.income.add', 'Agregar')}
          </Button>
        </Stack>

        {/* List of added sources */}
        {state.incomeSources.length > 0 && (
          <Box sx={{ mt: 2 }}>
            {state.incomeSources.map((source) => (
              <Box
                key={source.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 1,
                  px: 2,
                  mb: 1,
                  bgcolor: '#F7F9FC',
                  borderRadius: 2,
                }}
              >
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    {source.name}
                    {source.isGross && (
                      <Chip label={t('budgetPlanner.income.grossTag', 'Bruto')} size="small" sx={{ ml: 1 }} />
                    )}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {`${formatCAD(source.amount)} / ${freqLabels[source.frequency]} → ${formatCAD(toMonthly(source.amount, source.frequency))}/${t('budgetPlanner.common.month', 'mes')}`}
                  </Typography>
                </Box>
                <IconButton onClick={() => dispatch(removeIncome(source.id))} color="error" aria-label="Eliminar ingreso" sx={{ minWidth: 44, minHeight: 44 }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}

            <Box sx={{ mt: 2, p: 2, bgcolor: '#E8F5E9', borderRadius: 2 }}>
              <Typography variant="subtitle2" color="#2E7D32">
                {`${t('budgetPlanner.income.monthlyTotal', 'Total mensual')}: ${formatCAD(monthlyTotal)}`}
              </Typography>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
